import { useQuery } from '@apollo/client';
import { ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { gql } from '__generated__/gql';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';

import Badge from 'primitives/Badge';
import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';

import { getUser } from 'utils/auth';
import unitTransferReasonToReadable from 'utils/enums/unit-transfer-reason-to-readable';
import unitTransferSlaToBadge from 'utils/enums/unit-transfer-sla-to-badge';
import unitTransferStatusToReadable from 'utils/enums/unit-transfer-status-to-readable';

const MY_UNIT_TRANSFER_REQUESTS_QUERY = gql(`
  query MyUnitTransferRequests($cursor: ID, $limit: Int) {
    myUnitTransferRequests(cursor: $cursor, limit: $limit) {
      nodes {
        id
        status
        reason
        createdAt
        slaDaysRemaining
        transferor {
          id
          name
        }
        transferee {
          id
          name
        }
      }
      pageInfo {
        hasNextPage
        cursor
      }
    }
  }
`);

const OUTGOING = 'OUTGOING';
const INCOMING = 'INCOMING';

// Rendered as the Unit Transfers tab of the investment entity page.
export const UnitTransferListContent: FC = () => {
  const navigate = useNavigate();
  const user = getUser();

  const { loading, error, data, refetch, fetchMore } = useQuery(MY_UNIT_TRANSFER_REQUESTS_QUERY, {
    notifyOnNetworkStatusChange: true,
  });

  if (loading && !data) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  // Without a direction, someone sending units and someone receiving them saw identical rows.
  const requests = data.myUnitTransferRequests.nodes.map(request => {
    const viewerIsTransferor = request.transferor.id === user.id;
    return {
      ...request,
      direction: viewerIsTransferor ? OUTGOING : INCOMING,
      counterparty: viewerIsTransferor ? request.transferee : request.transferor,
    };
  });
  const pageInfo = data.myUnitTransferRequests.pageInfo;

  const table = (
    <DataTable
      data={requests}
      emptyListTitle="No unit transfer requests yet"
      emptyListDescription="Start a new transfer request to move units to another investor."
      onClick={request => navigate(`/invest/unit-transfers/${request.id}`)}
      columns={[
        {
          label: 'Direction',
          fieldName: 'direction',
          type: 'CUSTOM',
          customValue: value =>
            value === OUTGOING ? (
              <Badge label="Sending" color="gray" />
            ) : (
              <Badge label="Receiving" color="sky" />
            ),
        },
        {
          label: 'Status',
          fieldName: 'status',
          type: 'CUSTOM',
          customValue: value => {
            const { label, color } = unitTransferStatusToReadable(value);
            return <Badge label={label} color={color} showIndicator={true} />;
          },
        },
        {
          label: 'Counterparty',
          fieldName: 'counterparty.name',
        },
        {
          label: 'Reason',
          fieldName: 'reason',
          type: 'CUSTOM',
          customValue: value => unitTransferReasonToReadable(value),
        },
        {
          label: 'Due',
          fieldName: 'slaDaysRemaining',
          type: 'CUSTOM',
          customValue: value => {
            const slaBadge = unitTransferSlaToBadge(value);
            if (!slaBadge) return <span className="text-gray-400">-</span>;
            return <Badge label={slaBadge.label} color={slaBadge.color} />;
          },
        },
        {
          label: 'Created',
          fieldName: 'createdAt',
          type: 'DATE',
        },
      ]}
      hasNextPage={pageInfo.hasNextPage}
      onLoadMore={() =>
        fetchMore({
          variables: {
            cursor: pageInfo.cursor,
          },
        })
      }
    />
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 rounded-lg bg-indigo-50 p-5 ring-1 ring-indigo-100 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-white">
            <ArrowsRightLeftIcon className="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <p className="text-base font-semibold text-gray-900">Transfer your units</p>
            <p className="mt-1 text-sm text-gray-600">
              Move your units to another investor for a permitted reason, or track a transfer that
              is coming to you.
            </p>
          </div>
        </div>
        <div className="flex-shrink-0">
          <Button onClick={() => navigate('/invest/unit-transfers/new')}>
            New transfer request
          </Button>
        </div>
      </div>
      {table}
    </div>
  );
};
